<script setup lang="ts">
import { LOCALE_CODES } from '@yescapa-dev/ysc-components/i18n'
import {
  SCREEN_APP_EN,
  SCREEN_APP_FR,
  SCREEN_APP_ES,
  SCREEN_APP_CA,
  SCREEN_APP_IT,
  SCREEN_APP_DE,
  SCREEN_APP_PT,
  SCREEN_APP_NL,
} from '~/constants/pictures'

const { locale } = useI18n()
const findOneLocalizedContentWithFallback = useFindOneLocalizedContentWithFallback()
const { data, error } = await useAsyncData('page-get-apps', () => findOneLocalizedContentWithFallback('get-apps'))
if (error.value) {
  throw createError(error.value)
}

const screenImgLocalMap: Partial<Record<AvailableTopLevelLocalesCodes, string>> = {
  [LOCALE_CODES.EN]: SCREEN_APP_EN,
  [LOCALE_CODES.FR]: SCREEN_APP_FR,
  [LOCALE_CODES.ES]: SCREEN_APP_ES,
  [LOCALE_CODES.CA]: SCREEN_APP_CA,
  [LOCALE_CODES.IT]: SCREEN_APP_IT,
  [LOCALE_CODES.DE]: SCREEN_APP_DE,
  [LOCALE_CODES.NL]: SCREEN_APP_NL,
}

const screenImgSrc = computed(() => {
  // LOCALE_CODES.PT is 'pt-pt' so we dont want to use the getTopLevelLocale
  if (locale.value === LOCALE_CODES.PT) {
    return SCREEN_APP_PT
  }
  return screenImgLocalMap[getTopLevelLocale(locale.value as AvailableLocaleCodes)] ?? SCREEN_APP_EN
})
</script>

<template>
  <AppPageSection class="bg-peacock-50 quarter-circle">
    <!-- eslint-disable vue/no-v-html -->

    <div class="flex flex-col items-center gap-12 lg:flex-row">
      <div class="basis-1/2">
        <header>
          <h2
            class="relative mb-4 pb-4 font-semibold text-pink-500 after:absolute after:bottom-0 after:block after:h-1 after:w-20 after:bg-pink-500 after:content-[''] lg:mb-8"
          >
            {{ $t('pages.get-apps.suptitle') }}
          </h2>
          <div class="mb-8 lg:flex">
            <h1 class="text-3xl font-semibold lg:text-5xl">
              {{ $t('pages.get-apps.title') }}
            </h1>
          </div>
        </header>

        <ContentRenderer
          v-if="data"
          :value="data"
          class="prose sm:prose-lg prose-highlight mb-8"
        />

        <div class="mb-8 flex items-center gap-10">
          <nav
            :aria-label="$t('commons.navigation.mobile_apps_links')"
            class="not-prose flex flex-col flex-wrap items-center gap-10 lg:flex-row lg:first-letter:"
          >
            <NuxtLink
              to="https://apps.apple.com/us/app/yescapa/id1043554869"
              class="flex space-x-3 bg-white py-3 pl-3 pr-6 rounded-md"
            >
              <YscIconsApple class="h-12 w-12" />
              <span v-html="$t('components.app_footer.apps.apple_html')" />
            </NuxtLink>

            <NuxtLink
              to="https://play.google.com/store/apps/details?id=com.yescapa"
              class="flex space-x-3 bg-white py-3 pl-3 pr-6 rounded-md"
            >
              <YscIconsPlayStore class="h-12 w-12 p-1.5" />
              <span v-html="$t('components.app_footer.apps.play_store_html')" />
            </NuxtLink>
          </nav>
        </div>
      </div>
      <div class="hidden basis-1/2 lg:block">
        <div
          class="after:content relative mx-auto min-h-min max-w-sm rounded-[40px] border-[20px] border-black bg-black after:absolute after:left-1/4 after:right-1/4 after:top-0 after:h-7 after:rounded-b after:bg-black"
        >
          <TwicImg
            class="rounded-lg"
            ratio="1080/2340"
            :src="screenImgSrc"
            :alt="$t('pages.get-apps.screen-alternate')"
          />
        </div>
      </div>
    </div>
  </AppPageSection>
</template>

<style scoped>
.prose-highlight :deep(strong) {
  @apply bg-yellow-200;
}
</style>
